import { getBrazilCalendarMonthRange } from "./dateUtils";

export type DashboardCompetencePreset =
  | "past-3"
  | "past-6"
  | "past-12"
  | "span-3-3"
  | "span-3-6"
  | "span-3-12"
  | "custom";

export type DashboardCompetenceRange = {
  competenceDateFrom: string;
  competenceDateTo: string;
};

type YearMonth = { year: number; month: number };

function getBrazilCurrentYearMonth(): YearMonth {
  const { from } = getBrazilCalendarMonthRange();
  return {
    year: Number(from.slice(0, 4)),
    month: Number(from.slice(5, 7)),
  };
}

function addCalendarMonths(year: number, month: number, delta: number): YearMonth {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function monthToApiRange(year: number, month: number): DashboardCompetenceRange {
  const mm = pad2(month);
  const from = `${year}-${mm}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${year}-${mm}-${pad2(lastDay)}`;
  return { competenceDateFrom: from, competenceDateTo: to };
}

function rangeBetweenMonths(from: YearMonth, to: YearMonth): DashboardCompetenceRange {
  const start = monthToApiRange(from.year, from.month);
  const end = monthToApiRange(to.year, to.month);
  return {
    competenceDateFrom: start.competenceDateFrom,
    competenceDateTo: end.competenceDateTo,
  };
}

export function competenceRangeFromPreset(
  preset: Exclude<DashboardCompetencePreset, "custom">
): DashboardCompetenceRange {
  const current = getBrazilCurrentYearMonth();
  switch (preset) {
    case "past-3":
      return rangeBetweenMonths(addCalendarMonths(current.year, current.month, -3), current);
    case "past-6":
      return rangeBetweenMonths(addCalendarMonths(current.year, current.month, -6), current);
    case "past-12":
      return rangeBetweenMonths(addCalendarMonths(current.year, current.month, -12), current);
    case "span-3-3":
      return rangeBetweenMonths(
        addCalendarMonths(current.year, current.month, -3),
        addCalendarMonths(current.year, current.month, 3)
      );
    case "span-3-6":
      return rangeBetweenMonths(
        addCalendarMonths(current.year, current.month, -3),
        addCalendarMonths(current.year, current.month, 6)
      );
    case "span-3-12":
      return rangeBetweenMonths(
        addCalendarMonths(current.year, current.month, -3),
        addCalendarMonths(current.year, current.month, 12)
      );
  }
}

export function competenceRangeFromCustomMonths(
  fromMonth: string,
  toMonth: string
): DashboardCompetenceRange | null {
  const from = fromMonth.trim();
  const to = toMonth.trim();
  if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to)) return null;
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (fy > ty || (fy === ty && fm > tm)) return null;
  return rangeBetweenMonths({ year: fy, month: fm }, { year: ty, month: tm });
}

export const DASHBOARD_PERIOD_PRESETS: {
  id: Exclude<DashboardCompetencePreset, "custom">;
  label: string;
}[] = [
  { id: "past-3", label: "−3" },
  { id: "past-6", label: "−6" },
  { id: "past-12", label: "−12" },
  { id: "span-3-3", label: "−3 +3" },
  { id: "span-3-6", label: "−3 +6" },
  { id: "span-3-12", label: "−3 +12" },
];
